import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import { Dispatch } from 'src/app/classes/dispatch';
import { DispatchDriverSummary } from 'src/app/classes/dispatch-driver-summary';
import { DispatchService } from 'src/app/services/dispatch.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import moment from 'moment';
import { DispatchFilterParams } from 'src/app/classes/dispatch-filter-params';
import { debounceTime, distinctUntilChanged, filter, skip } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { DispatchSaveFilterDialogComponent } from '../dispatch-save-filter-dialog/dispatch-save-filter-dialog.component';
import { TimePickerComponent } from '../../time-picker/time-picker.component';

const SAVED_FILTERS_KEY = 'savedFilters';

@Component({
  standalone: false,
  selector: 'app-dispatch-filter',
  templateUrl: './dispatch-filter.component.html',
  styleUrls: [ './dispatch-filter.component.scss' ]
})
export class DispatchFilterComponent implements OnInit, OnDestroy {
  hourOffsets = [ ...Array(47).keys() ].map((key: number) => key - 23);
  drivers: DispatchDriverSummary[] = [];
  isLoadingFilter = false;
  savedFilters: DispatchFilterParams[] = JSON.parse(localStorage.getItem(SAVED_FILTERS_KEY)) || [];
  minFilterDate: string = moment.utc().subtract(2, 'weeks').toISOString();

  taskStatusOpts: { name: string; header: string }[] = [
    { name: 'Unassigned', header: 'Unassigned' },
    { name: 'Assigned', header: 'Assigned' },
    { name: 'Started', header: 'Started' },
    { name: 'Completed', header: 'Completed' },
    { name: 'Cancelled', header: 'Cancelled' }
  ];

  taskTypeOpts: { name: string; header: string }[] = [
    { name: 'InboundFlight', header: 'Flt Inbound' },
    { name: 'OutboundFlight', header: 'Flt Outbound' },
    { name: 'Other', header: 'Other' },
    { name: 'RemoteParking', header: 'Remote Parking' },
    { name: 'Terminal', header: 'Terminal' }
  ];

  filterForm: FormGroup = this.fb.group({
    taskStatus: this.fb.array(this.taskStatusOpts.map((x) => !1)),
    taskTypes: this.fb.array(this.taskTypeOpts.map((x) => !1)),
    fltStatus: this.fb.control(''),
    driver: this.fb.control(''),
    dispatchDate: this.fb.control(moment().startOf('day').toISOString()),
    offsetHours: this.fb.control(0),
    searchText: this.fb.control('')
  });

  subs = new Subscription();
  private suppressAutoApply = false;

  @ViewChild(TimePickerComponent) datePicker: TimePickerComponent;

  constructor(
    readonly fb: FormBuilder,
    readonly dispatchService: DispatchService,
    readonly utilityService: UtilitiesService,
    readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.subs.add(this.subToDispatchData());
    this.subs.add(this.subToSharedDispatchFilterParams());
    this.subs.add(this.subToFilterFormChanges());

    if (this.dispatchService.dispatchFilterFormVals) {
      this.applySavedFilter(this.dispatchService.dispatchFilterFormVals);
    }
  }

  subToFilterFormChanges(): Subscription {
    return this.filterForm.valueChanges
      .pipe(
        skip(1),
        debounceTime(300),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe(() => {
        if (this.suppressAutoApply) {
          this.suppressAutoApply = false;
          return;
        }
        this.dispatchService.isDispatchFilterOn = true;
        this.refreshDispatchData();
      });
  }

  ngOnDestroy(): void {
    this.dispatchService.dispatchFilterFormVals = this.dispatchService.isDispatchFilterOn ? this.filterForm.value : undefined;
    this.subs.unsubscribe();
  }

  subToDispatchData(): Subscription {
    return this.dispatchService.getDispatchData().subscribe((dispatchData: Dispatch) => {
      if (dispatchData) {
        this.drivers = dispatchData.driverSummaryList;
      }
      this.isLoadingFilter = false;
    });
  }

  filterFltStatus(): void {
    this.dispatchService.isDispatchFilterOn = true;
    this.refreshDispatchData();
  }

  subToSharedDispatchFilterParams(): Subscription {
    return this.dispatchService.dispatchFilterParams
      .pipe(filter((params: DispatchFilterParams) => !!params))
      .subscribe((params: DispatchFilterParams) => {
        const driverCtrl: FormControl = this.filterForm.get('driver') as FormControl;
        if (params.driver && params.driver !== driverCtrl.value) {
          this.filterForm.get('driver').setValue(params.driver);
        }
      });
  }

  toggleApplyFilter(): void {
    this.dispatchService.isDispatchFilterOn = !this.dispatchService.isDispatchFilterOn;

    if (!this.dispatchService.isDispatchFilterOn) {
      this.suppressAutoApply = true;
      this.resetFilterForm();
    }

    this.refreshDispatchData();
  }

  convertToValue(key: string, data: any[]): any {
    return this.filterForm.value[key].map((x, i) => x && data[i].name).filter(Boolean);
  }

  externalFilterApply(): void {
    this.dispatchService.isDispatchFilterOn = true;
  }

  refreshDispatchData(): void {
    const taskStatusArr = this.convertToValue('taskStatus', this.taskStatusOpts);
    const taskTypesArr = this.convertToValue('taskTypes', this.taskTypeOpts);
    const tasks = [ ...taskStatusArr, ...taskTypesArr ];
    const taskTypes = tasks && tasks.length ? `${tasks.join('&type=')}` : '';
    const dateFilter = this.filterForm.get('dispatchDate').value || '';

    this.dispatchService.dispatchFilterParams.next({
      driver: this.filterForm.get('driver').value ? this.filterForm.get('driver').value : '',
      date: dateFilter === moment().startOf('day').toISOString() ? '' : dateFilter,
      hoursOffset: this.filterForm.get('offsetHours').value || 0,
      searchText: this.filterForm.get('searchText').value || '',
      type: this.filterForm.get('fltStatus').value ? this.filterForm.get('fltStatus').value : taskTypes
    });
    this.isLoadingFilter = true;
    this.dispatchService.triggerDispatchUpdate();
  }

  resetFilter(): void {
    this.datePicker.dateTimeString = moment().startOf('day').toISOString();
    this.resetFilterForm();
    this.dispatchService.isDispatchFilterOn = false;
    this.refreshDispatchData();
  }

  resetFilterForm(): void {
    this.filterForm.reset({
      taskStatus: this.taskStatusOpts.map((x) => !1),
      taskTypes: this.taskTypeOpts.map((x) => !1),
      fltStatus: '',
      driver: '',
      dispatchDate: moment().startOf('day').toISOString(),
      offsetHours: 0,
      searchText: ''
    });
  }

  updateTaskTypes(event): void {
    let data: any[] = this.filterForm.controls['taskTypes'].value;
    if (!data) {
      data = [];
    }
    if (event.checked) {
      data.push(event.source.value);
    } else {
      const index = data.indexOf(event.source.value);
      data.splice(index, 1);
    }
    this.filterForm.controls['taskTypes'].setValue(data);
  }

  getSelectedTask(): string {
    const count =
      this.filterForm.controls.taskStatus.value.filter(Boolean).length +
      this.filterForm.controls.taskTypes.value.filter(Boolean).length;
    if (count && count > 0) {
      return count + ' selected';
    }
    return 'Type';
  }

  getDriverSummaryDisplayName(driverSummary: DispatchDriverSummary): string {
    return this.utilityService.getDriverSummaryDisplayName(driverSummary);
  }

  setFilterDate(date: string): void {
    this.filterForm.get('dispatchDate').setValue(moment(date).startOf('day').toISOString());
  }

  saveFilter(): void {
    this.dialog.open(DispatchSaveFilterDialogComponent).afterClosed().subscribe((filterName: string) => {
      if (filterName) {
        this.savedFilters.push({ ...this.filterForm.value, filterName });
        localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(this.savedFilters));
      }
    });
  }

  deleteSavedFilter(filterFormVals: any): void {
    this.savedFilters.splice(
      this.savedFilters.findIndex((savedFilter: any) => JSON.stringify(savedFilter) === JSON.stringify(filterFormVals)),
      1
    );
    localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(this.savedFilters));
  }

  applySavedFilter(filterFormVals: any): void {
    this.filterForm.patchValue(filterFormVals);
    this.dispatchService.isDispatchFilterOn ? this.refreshDispatchData() : this.toggleApplyFilter();
  }
}
